import JoiBase from 'joi';
import joiDate from '@joi/date';
import capitalize from 'lodash/capitalize';
import lowerCase from 'lodash/lowerCase';
import cloneDeep from 'lodash/cloneDeep';
import { stringExtension, arrayExtension, dateExtensions } from '../util/joiHelpers';
/**
 * @param  {} word
 */
const prettyPrint = (word) => {
    const last = word.split('::').pop();
    const words = last?.match(/[A-Za-z][a-z]*/g) || [];
    return words.map(capitalize).join(' ');
};
const Joi = JoiBase.extend(joiDate)
    .extend(stringExtension)
    .extend(arrayExtension)
    .extend(dateExtensions);
const rules = {
    NUMBER: {
        max: (val = 0) => Joi.number().max(val),
        min: (val = 0) => Joi.number().min(val),
        multipleOf: (val = 1) => Joi.number().multiple(val),
        isInteger: () => Joi.number().integer(),
    },
    STRING: {
        minLength: (val = 0) => Joi.string().min(val),
        maxLength: (val = 0) => Joi.string().max(val),
        length: (val = 0) => Joi.string().length(val),
        maxWordCount: (val = 0) => Joi.string().maxWordCount(val),
        minWordCount: (val = 0) => Joi.string().minWordCount(val),
        startsWith: (val = '') => Joi.string().startsWith(val),
        endsWith: (val = '') => Joi.string().endsWith(val),
        regex: (val = '') => Joi.string().pattern(new RegExp(val)),
        upperCase: (val) => (val ? Joi.string().uppercase() : Joi.string().lowercase()),
        lowerCase: (val) => (val ? Joi.string().lowercase() : Joi.string().uppercase()),
        allowEmptyString: (val) => (val ? Joi.string().allow('') : Joi.string().invalid('')),
        isRequired: (val) => (val ? Joi.string().required() : Joi.string().optional().allow(null, '')),
    },
    DATE: {
        format: (val = 'MM/DD/YYYY') => Joi.date().format(val),
        min: (val) => Joi.date().min(val),
        max: (val) => Joi.date().max(val),
        greaterThan: (val) => Joi.date().greater(val),
        lessThan: (val) => Joi.date().less(val),
        minAge: (val = 0) => Joi.date().minAge(val),
        maxAge: (val = 0) => Joi.date().maxAge(val),
        isRequired: (val) => (val ? Joi.date().required() : Joi.date().optional().allow(null, '')),
    },
    ARRAY: {
        minLength: (val = 0) => Joi.array().min(val),
        maxLength: (val = 0) => Joi.array().max(val),
        length: (val = 0) => Joi.array().length(val),
        items: (val, func) => Joi.array().items(func(val)),
    },
    SHARED: {
        allowedValues: (val = [1]) => {
            if (typeof val === 'string') { // if a reference string is passed
                return Joi.any().valid(Joi.in(val, { render: true }));
            }
            else if (Joi.isRef(val)) { // if a reference object is passed
                return Joi.any().valid(Joi.in(`$${val.key}`, { render: true }));
            }
            // otherwise, a raw array should have been passed
            return Joi.any().valid(...val);
        },
        allowedValuesWithCustomError: (val = [1]) => {
            // a raw array should have been passed
            const values = val.join('|');
            return Joi.string().pattern(new RegExp(values));
        },
        isRequired: (val) => (val ? Joi.any().required() : Joi.any().optional().allow(null)),
        value: (val) => Joi.any().valid(val),
        raw: (val) => (val ? Joi.any().raw() : Joi.any()),
        allowNull: (val) => (val ? Joi.any().allow(null) : Joi.any().invalid(null)),
        isForbidden: (val) => (val ? Joi.any().forbidden() : Joi.any()),
        conditions: (val, _, fieldType) => {
            const conditionsSchema = val.reduce((acc, current) => {
                const { condition: { key, rules: conditionRules }, then: starterThen, otherwise: starterOtherwise } = current;
                // eslint-disable-next-line no-use-before-define, @typescript-eslint/no-use-before-define
                const is = singleInputToJoiSchema(conditionRules);
                const then = {
                    ...cloneDeep(starterThen),
                    type: fieldType,
                };
                const obj = {
                    is,
                    // eslint-disable-next-line no-use-before-define, @typescript-eslint/no-use-before-define
                    then: singleInputToJoiSchema(then),
                };
                if (starterOtherwise) {
                    // eslint-disable-next-line no-use-before-define, @typescript-eslint/no-use-before-define
                    obj.otherwise = singleInputToJoiSchema({
                        ...cloneDeep(starterOtherwise),
                        type: fieldType,
                    });
                }
                // we force absolute paths here
                // can revisit in the future if we want to be more flexible
                return acc.when(`/${key}`, obj);
            }, Joi.any());
            return conditionsSchema;
        },
    },
};
// helper functions
const flattenJoiChecks = (running, current) => running.concat(current);
const JOI_TYPE_ALIAS = {
    ENUM: 'any',
    FIELDS: 'object',
    DICTIONARY: 'object',
};
/**
* Checks if a value is an ION reference or not (such as '$WHO_VALUES')
 * @private
 * @param  {any} needle - the value to check
 * @returns {Boolean}
 */
const isRef = (needle) => typeof needle === 'string' && needle.charAt(0) === '$';
/**
* A helper to check whether to prioritize rules.isRequired or conditional isRequired vs. field.isRequired
 * @private
 * @param  {Object} field - the field to check for isRequired props
 * @returns {Boolean}
 */
const shouldUseTopLevelIsRequired = (field) => {
    // isRequired could have 'false' as its value, we're checking if the property exists here.
    const hasIsRequired = (obj) => obj && Object.hasOwnProperty.call(obj, 'isRequired');
    const hasConditionalIsRequired = field.rules?.conditions?.find(({ then, otherwise }) => hasIsRequired(then) || hasIsRequired(otherwise));
    const hasIsRequiredInRules = !!(hasIsRequired(field.rules) || hasConditionalIsRequired);
    if (hasIsRequiredInRules) {
        return false;
    }
    return true;
};
const singleInputToJoiSchema = (allRules) => {
    const { type = 'SHARED', ...remainingRules } = allRules;
    const finalRules = [...Object.keys(remainingRules)]
        .map((key) => {
        // first, check the type-specific rule
        // otherwise, check if it's a shared rule
        const func = rules[type]?.[key] || rules.SHARED[key];
        if (!func) {
            throw new Error(`Function not found for rule with key: ${key}.`);
        }
        const valIsObj = typeof allRules[key] === 'object' && allRules[key] !== null && allRules[key].length === undefined;
        const valToUse = valIsObj ? allRules[key].value : allRules[key];
        // make sure if it's ref to use the ref value, not the raw
        const val = isRef(valToUse)
            ? Joi.ref(valToUse, { render: true })
            : valToUse;
        // if we set a customer error, use that as the message, not the default
        if (valIsObj && (allRules.rules?.[key].error || allRules[key].error)) {
            return func(val).message(allRules.rules?.[key]?.error || allRules[key].error);
        }
        return func(val);
    })
        // now we flatten out all the rules using concat
        .reduce(flattenJoiChecks, (Joi[JOI_TYPE_ALIAS[type] || lowerCase(type) || 'any'])());
    return finalRules;
};
/**
 * Creates the JOI schema object to validate field inputs.
 * @param  {import('../model').Field[]} fields - an array of fields
 * @returns {Object} - A JOI schema object that can be run against validations
 * @memberof module:ionHelpers
 */
const ION_TO_JOI = (fields) => {
    // function to setup all the Joi calls
    const runner = (_fields) => {
        const o = {};
        const attachRules = (input) => {
            if (!input) {
                return null;
            }
            if (input?.rules) {
                const finalRules = [...Object.keys(input.rules)]
                    .map((key) => {
                    // first, check the type-specific rule
                    // otherwise, check if it's a shared rule
                    const func = rules[input.type]?.[key] || rules.SHARED[key];
                    const valIsObj = typeof input.rules[key] === 'object' && input.rules[key] !== null && input.rules[key].length === undefined;
                    const valToUse = valIsObj ? input.rules[key].value : input.rules[key];
                    // make sure if it's ref to use the ref value, not the raw
                    // IONs should have refs subbed in before ITJ is used, however leaving this here just in case.
                    const val = isRef(valToUse)
                        ? Joi.ref(valToUse, { render: true })
                        : valToUse;
                    // if we set a custom error, use that as the message, not the default
                    if (valIsObj && input.rules[key].error) {
                        return func(val, runner).message(input.rules[key] && input.rules[key].error);
                    }
                    return func(val, runner, input.type);
                })
                    // now we flatten out all the rules using concat
                    .reduce(flattenJoiChecks, (Joi[JOI_TYPE_ALIAS[input.type] || lowerCase(input.type) || 'any'])());
                return finalRules;
            }
            return (Joi[JOI_TYPE_ALIAS[input.type] || lowerCase(input.type) || 'any'])();
        };
        const describeInput = (input) => {
            // if it's a sub-object, apply rules to top level obj then recurse through its internal fields.
            if (input.type === 'FIELDS') {
                o[input.id] = attachRules(input).concat(Joi.object().keys(runner(input.value)));
            }
            else {
                o[input.id] = attachRules(input);
            }
            // If we should use top level isRequired prop (vs. isRequired inside the 'rules' prop)
            // We add the required/optional bit here, otherwise, they'll get added as part of "attachRules"
            // Preference is given to isRequired inside "rules."
            // Note: if a rule isForbidden is true, it will have a top level isRequired of
            // false. Need to add a check here to check if the isForbidden rule is true,
            // if it is, set the field to forbidden.
            if (shouldUseTopLevelIsRequired(input)) {
                if (input.isRequired) {
                    o[input.id] = o[input.id].concat(Joi.any().required());
                }
                else if (input.rules?.isForbidden) {
                    o[input.id] = o[input.id].concat(Joi.any().forbidden());
                }
                else {
                    o[input.id] = o[input.id].concat(['STRING', 'DATE'].includes(input.type)
                        ? Joi.any().optional().allow(null, '')
                        : Joi.any().optional().allow(null));
                }
            }
            if (Object.prototype.hasOwnProperty.call(input, 'defaultValue')) {
                o[input.id] = o[input.id].concat(Joi.any().default(input.defaultValue));
            }
            // add the label
            o[input.id] = o[input.id].concat(Joi.any().label(input?.uiDisplay?.label || prettyPrint(input.id)));
        };
        // where are we starting with the initial parameter?
        if (Array.isArray(_fields)) {
            _fields.forEach(describeInput);
            return o;
        }
        else if (_fields && _fields.type === 'FIELDS') {
            (_fields.value || [{ ok: 1 }]).forEach(describeInput);
            return o;
        }
        const returnedRules = attachRules(_fields);
        return returnedRules;
    };
    const keys = runner(fields);
    return Joi.object().keys(keys);
};
export default ION_TO_JOI;
